import { createSlice } from '@reduxjs/toolkit'
import { format } from 'date-fns'

import history from '@/config/router/history'
import { startAppListening } from '@/config/store/listenerMiddleware'
import { startInitialization } from '@/ducks/app/slices/app.shared'
import { authApi } from '@/ducks/auth/api/auth.api'
import { Permission, StoreLoginAs } from '@/ducks/auth/auth.types'
import {
  readFromLocalStorage,
  removeFromLocalStorage,
  setToLocalStorage
} from '@/services/localStorage'
import { setClarityPostLogin } from '@/utils/clarity.utils'
import { logFirebaseEvent } from '@/utils/firebase.utils'

const LOGIN_PATH = '/login'
const ORDERS_PATH = '/orders'

export interface AuthState {
  token?: string | null
  authSource?: string | null
  login_as?: StoreLoginAs | null
  permissions: Permission[]
  rolesLoaded: boolean
}

export const initialState = {
  token: undefined,
  authSource: undefined,
  login_as: undefined as StoreLoginAs | undefined,
  permissions: [],
  rolesLoaded: false
}

const initializeState = (): AuthState => ({
  token: readFromLocalStorage('token') || undefined,
  authSource: readFromLocalStorage('authSource') || undefined,
  login_as: undefined,
  permissions: [],
  rolesLoaded: false
})

const authSlice = createSlice({
  name: 'auth',
  initialState: initializeState,
  reducers: {
    logout: () => {
      return { ...initialState }
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        state.token = payload.token
        state.authSource = payload.authSource || null
        state.login_as = null
        state.permissions = []
        state.rolesLoaded = false
      }
    )
    builder.addMatcher(
      authApi.endpoints.getMyRoles.matchFulfilled,
      (state, { payload }) => {
        state.permissions = payload.permissions
        state.rolesLoaded = true
        if (payload.login_as === 'owner' || payload.login_as === 'store_user') {
          state.login_as = payload.login_as
        }
      }
    )
    builder.addMatcher(authApi.endpoints.getMyRoles.matchRejected, state => {
      state.rolesLoaded = true
    })
  }
})

startAppListening({
  actionCreator: authSlice.actions.logout,
  effect: async function syncStorage(_, listenerApi) {
    listenerApi.dispatch(authApi.util.resetApiState())
    removeFromLocalStorage('token', 'authSource')

    history.push(LOGIN_PATH)
  }
})

startAppListening({
  matcher: authApi.endpoints.login.matchFulfilled,
  effect: async (action, listenerApi) => {
    setToLocalStorage({
      authSource: action.payload.authSource || null
    })
    history.push(ORDERS_PATH)
    listenerApi.dispatch(startInitialization())

    if (action?.payload?.id) {
      logFirebaseEvent('AC_Web_ActivatedAccount', {
        Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
        Store_ID: action.payload.id
      })

      setClarityPostLogin({
        storeId: action.payload.id,
        storeType: action.payload.type,
        currencyCode: action.payload.currencyCode
      })
    }
  }
})

export const { logout } = authSlice.actions

export const authInitialState = initialState
export default authSlice.reducer
